import { axiosWithConfig } from '@assembly/config/axios'

// Types based on OpenAPI spec
export enum UsageType {
  LLM_INPUT_TOKENS = 'LLM_INPUT_TOKENS',
  LLM_OUTPUT_TOKENS = 'LLM_OUTPUT_TOKENS',
  CONVERSATION_VOICE = 'CONVERSATION_VOICE',
}

export interface UsageEventRequest {
  assemblyId: string
  usageType: UsageType
  value: number
}

export interface StatusMessage {
  status: 'SUCCESS' | 'ERROR' | 'BAD_REQUEST' | 'FORBIDDEN' | 'NOT_FOUND'
  message: string
}

export interface UsageDataItem {
  total: number
  start_time?: number // Legacy snake_case format
  end_time?: number // Legacy snake_case format
  startTime?: number // New camelCase format
  endTime?: number // New camelCase format
}

export interface UsageMetadata {
  scheduleId?: string
  startDate?: number
  status?: string
}

export interface UsageDataResponse {
  metadata?: UsageMetadata
  llm_tokens?: UsageDataItem // Legacy format
  conversation_voice?: UsageDataItem // Legacy format
  [key: string]: UsageDataItem | UsageMetadata | undefined // For price_xxxxx keys and metadata
}

export interface InvoiceLineItem {
  id: string
  amount: number
  amountExcludingTax: number
  currency: string
  description: string
  quantity: number
  type: string
  period?: {
    start: number
    end: number
  }
  price?: {
    id: string
    nickname?: string | null
    unitAmount: number | null
    unitAmountDecimal?: number | null
    recurring?: {
      usageType?: string
    }
  }
  plan?: {
    id: string
    nickname?: string | null
    amount: number | null
    amountDecimal?: number | null
    usageType?: string
  }
}

export interface StripeListObject {
  object: 'list'
  data: InvoiceLineItem[]
  hasMore?: boolean
  url?: string
  [key: string]: any
}

export interface InvoicePreviewResponse {
  id: string
  amount: number
  currency: string
  status: string
  lineItems?: InvoiceLineItem[] | StripeListObject
  data?: InvoiceLineItem[] | StripeListObject // Some APIs return data array
}

export interface SubscriptionItem {
  id: string
  priceId: string
  unitAmount: number
  currency: string
  usageType: 'metered' | null
}

export interface UsageBillingInfo {
  llmInputTokensMeterId?: string | null
  llmInputTokensPriceId?: string | null
  llmOutputTokensMeterId?: string | null
  llmOutputTokensPriceId?: string | null
  conversationVoiceMeterId?: string | null
  conversationVoicePriceId?: string | null
}

export interface ProductInfo {
  name?: string
  sku?: 'ASSEMBLY_ORCHESTRATION' | 'ASSEMBLY_PARTNERS' | 'ASSEMBLY_AGENT_SUBSCRIPTION' | 'FOUNDRY_APP'
  projectType?: string
  assemblyId?: string
  orgId?: string
}

export interface SubscriptionInfoResponse {
  subscriptionScheduleId?: string
  status?: 'not_started' | 'active' | 'completed' | 'released' | 'canceled'
  startDate?: number
  endBehavior?: string
  subscriptionId?: string
  subscriptionStatus?: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid'
  currentPeriodStart?: number
  currentPeriodEnd?: number
  items?: SubscriptionItem[]
  usageBilling?: UsageBillingInfo
  customerId?: string
  productId?: string
  productInfo?: ProductInfo | null
  basePriceId?: string
  setupComplete?: boolean
}

export interface AssemblyLineInfo {
  id: string
  name: string
  description?: string | null
  orgId: string
  projectType?: string | null
  sku?: 'ASSEMBLY_ORCHESTRATION' | 'ASSEMBLY_PARTNERS' | 'ASSEMBLY_AGENT_SUBSCRIPTION' | 'FOUNDRY_APP' | null
  startDate?: number | null
  billingAmount?: number | null
  currency?: string | null
}

export interface ProductDetails {
  id: string
  name: string
  description?: string | null
  metadata?: Record<string, string>
  active?: boolean
  created?: number
}

export interface SubscriptionSummary {
  setupComplete: boolean
  hasUsageBilling: boolean
}

export interface TokenPricing {
  inputTokensPerMillion?: number
  outputTokensPerMillion?: number
  voiceMinutesPerMinute?: number
  totalCredits?: number | null
  totalCreditsMonetaryValue?: number | null
  creditsStartTime?: number | null
  creditsEndTime?: number | null
}

export interface CreditBalanceInfo {
  availableBalance?: number | null
  availableBalanceInDollars?: number | null
  ledgerBalance?: number | null
  ledgerBalanceInDollars?: number | null
  currency?: string | null
  totalGrants?: number | null
}

export interface SubscriptionDetailsResponse {
  hasSubscription: boolean
  assemblyLine: AssemblyLineInfo
  subscription?: SubscriptionInfoResponse | null
  product?: ProductDetails | null
  currentUsage?: UsageDataResponse | null
  summary?: SubscriptionSummary
  message?: string | null
  tokenPricing?: TokenPricing | null
  creditBalance?: CreditBalanceInfo | null
}

export interface CreditGrant {
  id: string
  customerId: string
  amount: number
  currency: string
  category: 'PAID' | 'PROMOTIONAL'
  state: 'PENDING' | 'GRANTED' | 'DEPLETED' | 'EXPIRED' | 'VOIDED'
  effectiveAt?: number | null
  expiresAt?: number | null
  createdAt: number
  updatedAt?: number | null
  name?: string | null
  priority?: number | null
  metadata?: Record<string, string>
}

export interface CreditBalance {
  ledgerBalance: number
  availableBalance: number
  currency: string
  ledgerBalanceInDollars?: number | null
  availableBalanceInDollars?: number | null
}

export interface CreditBalanceSummary {
  customerId: string
  balance: CreditBalance
  grants: CreditGrant[]
}

export interface CreditGrantRequest {
  amountInCents: number
  currency?: string
  category?: 'paid' | 'promotional'
  name?: string | null
  effectiveAt?: number | null
  expiresAt?: number | null
  priority?: number | null
  metadata?: Record<string, string>
}

export interface CreditGrantUpdateRequest {
  expiresAt?: number | null
  metadata?: Record<string, string>
}

// API Functions
export const usageBillingApi = {
  // Record usage event
  recordUsage: (data: UsageEventRequest) =>
    axiosWithConfig.post<StatusMessage>('/payments/usage/record', data),

  // Get current usage
  getCurrentUsage: (assemblyId: string, usageType?: UsageType) => {
    const params = usageType ? `?usageType=${usageType}` : ''
    return axiosWithConfig.get<UsageDataResponse>(`/payments/usage/${assemblyId}/current${params}`)
  },

  // Get usage for custom time period
  getUsageForPeriod: (assemblyId: string, startTimestamp: number, endTimestamp: number, usageType?: UsageType) => {
    let params = `?startTimestamp=${startTimestamp}&endTimestamp=${endTimestamp}`
    if (usageType) {
      params += `&usageType=${usageType}`
    }
    return axiosWithConfig.get<UsageDataResponse>(`/payments/usage/${assemblyId}/period${params}`)
  },

  // Get invoice preview
  getInvoicePreview: (assemblyId: string) =>
    axiosWithConfig.get<InvoicePreviewResponse>(`/payments/usage/invoice/${assemblyId}/preview`),

  // Create subscription and usage-based billing
  createSubscriptionAndUsageBilling: (assemblyId: string) =>
    axiosWithConfig.post<SubscriptionInfoResponse>(`/payments/usage/${assemblyId}/create-subscription-and-usage-billing`),

  // Clear subscription
  clearSubscription: (assemblyId: string) =>
    axiosWithConfig.delete<StatusMessage>(`/payments/usage/${assemblyId}/clear-subscription`),

  // Add usage-based billing to existing subscription
  addUsageBillingToSubscription: (assemblyId: string) =>
    axiosWithConfig.post<SubscriptionInfoResponse>(`/payments/usage/${assemblyId}/add-usage-billing-to-subscription`),

  // Create monthly subscription (without usage billing)
  createMonthlySubscription: (assemblyId: string) =>
    axiosWithConfig.post<SubscriptionInfoResponse>(`/payments/usage/${assemblyId}/create-monthly-subscription`),

  // Get subscription details
  getSubscriptionDetails: (assemblyId: string) =>
    axiosWithConfig.get<SubscriptionDetailsResponse>(`/payments/usage/${assemblyId}/subscription-details`),

  // Credit Grant operations
  createCreditGrant: (assemblyId: string, data: CreditGrantRequest) =>
    axiosWithConfig.post<CreditGrant>(`/payments/usage/${assemblyId}/credits/grant`, data),

  getCreditBalanceSummary: (assemblyId: string) =>
    axiosWithConfig.get<CreditBalanceSummary>(`/payments/usage/${assemblyId}/credits/balance`),

  listCreditGrants: (assemblyId: string) =>
    axiosWithConfig.get<CreditGrant[]>(`/payments/usage/${assemblyId}/credits/grants`),

  expireCreditGrant: (assemblyId: string, creditGrantId: string) =>
    axiosWithConfig.post<StatusMessage>(`/payments/usage/${assemblyId}/credits/grant/${creditGrantId}/expire`),

  voidCreditGrant: (assemblyId: string, creditGrantId: string) =>
    axiosWithConfig.post<StatusMessage>(`/payments/usage/${assemblyId}/credits/grant/${creditGrantId}/void`),

  updateCreditGrant: (assemblyId: string, creditGrantId: string, data: CreditGrantUpdateRequest) =>
    axiosWithConfig.put<CreditGrant>(`/payments/usage/${assemblyId}/credits/grant/${creditGrantId}`, data),
}

